import { ArrowRight } from "lucide-react";
import type { ReactNode } from "react";
import {
  AccordionContent,
  AccordionItem,
  AccordionPrimitive,
  AccordionTrigger,
} from "../../elements/accordion/accordion";
import { Button } from "../../elements/button/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../elements/select/select";
import { Text } from "../../elements/text/text";

/**
 * Cart Order Summary Component
 *
 * Displays a comprehensive order summary card with:
 * - Header with title and article count
 * - Optional order purpose section (dropdown, readonly, or hidden)
 * - Price breakdown (full details or just total)
 * - Action button (Checkout or Validate order)
 *
 * Responsive design using Tailwind breakpoints for mobile/desktop layouts.
 */

export interface CartOrderSummaryPrices {
  subtotal: string;
  shipping: string;
  vat: string;
  total: string;
}

export interface CartOrderSummaryOrderPurpose {
  mode: "dropdown" | "readonly" | "hidden";
  label: string;
  value?: string;
  options?: Array<{ value: string; label: string }>;
  onChange?: (value: string) => void;
}

export interface CartOrderSummaryAction {
  label: string;
  onClick: () => void;
  icon?: ReactNode;
  disabled?: boolean;
}

export interface CartOrderSummarySavings {
  totalSavings: string;
  totalSavingsValue: number;
  standardDealerPrice: string;
  annualDiscountSavings: string;
  annualDiscountSavingsValue: number;
  promoSavings: string;
  promoSavingsValue: number;
  extraDiscounts: string;
  extraDiscountsValue: number;
}

export interface CartOrderSummaryMicrocopies {
  subtotalLabel: string;
  shippingLabel: string;
  vatLabel: string;
  totalLabel: string;
  totalAmountLabel: string;
  yourTotalSavingsLabel: string;
  savingsDescriptionText: string;
  standardDealerPriceLabel: string;
  annualDiscountSavingsLabel: string;
  promoSavingsLabel: string;
  extraDiscountsLabel: string;
}

export interface CartOrderSummaryProps {
  title: string;
  articleCountText: string;
  orderPurpose?: CartOrderSummaryOrderPurpose;
  prices: CartOrderSummaryPrices;
  showFullPrices?: boolean;
  savings?: CartOrderSummarySavings;
  action: CartOrderSummaryAction;
  microcopies: CartOrderSummaryMicrocopies;
  className?: string;
}

export function CartOrderSummary(props: Readonly<CartOrderSummaryProps>) {
  const showFullPrices = props.showFullPrices ?? true;
  const showOrderPurpose = props.orderPurpose?.mode !== "hidden";
  const showSavingsSection =
    props.savings != null && props.savings.totalSavingsValue > 0;
  const showAnnualDiscountRow =
    props.savings != null && props.savings.annualDiscountSavingsValue > 0;
  const showPromoSavingsRow =
    props.savings != null && props.savings.promoSavingsValue > 0;
  const showExtraDiscountsRow =
    props.savings != null && props.savings.extraDiscountsValue > 0;
  const readOnlyOption =
    props.orderPurpose?.options?.find(
      (option) => option.value === props.orderPurpose?.value
    )?.label ?? props.orderPurpose?.value;

  return (
    <div
      className={`flex w-full flex-col rounded-md border border-stone-200 bg-white md:min-w-md ${props.className || ""}`}
    >
      {/* Header Section */}
      <div className="flex flex-col justify-center border-stone-200 border-b p-4">
        <div className="text-zinc-900">
          <Text variant="body-lg">{props.title}</Text>
        </div>
        <div className="text-zinc-900">
          <Text variant="body-md">{props.articleCountText}</Text>
        </div>
      </div>

      {/* Content Section */}
      <div className="flex flex-col gap-5 p-4">
        {/* Order Purpose Section */}
        {showOrderPurpose && props.orderPurpose && (
          <div className="flex flex-row items-center justify-between gap-2">
            <div className="flex-1 text-zinc-900">
              <Text variant="body-md">{props.orderPurpose.label}</Text>
            </div>

            {props.orderPurpose.mode === "dropdown" ? (
              <div className="w-full flex-1">
                <Select
                  onValueChange={props.orderPurpose.onChange}
                  value={props.orderPurpose.value || ""}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder={props.orderPurpose.value} />
                  </SelectTrigger>
                  <SelectContent>
                    {props.orderPurpose.options?.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <div className="flex-1 text-right text-zinc-900">
                <Text variant="body-md">{readOnlyOption}</Text>
              </div>
            )}
          </div>
        )}

        {/* Prices Section */}
        <div className="flex flex-col gap-0.5">
          {showFullPrices ? (
            <>
              <div className="flex flex-col gap-0">
                {/* Subtotal */}
                <div className="flex flex-row items-center justify-between">
                  <div className="text-zinc-900">
                    <Text variant="body-md">
                      {props.microcopies.subtotalLabel}
                    </Text>
                  </div>
                  <div className="text-right text-zinc-900">
                    <Text variant="body-md-bold">{props.prices.subtotal}</Text>
                  </div>
                </div>

                {/* Shipping */}
                <div className="flex flex-row items-center justify-between">
                  <div className="text-zinc-900">
                    <Text variant="body-md">
                      {props.microcopies.shippingLabel}
                    </Text>
                  </div>
                  <div className="text-right text-zinc-900">
                    <Text variant="body-md">{props.prices.shipping}</Text>
                  </div>
                </div>

                {/* VAT */}
                <div className="flex flex-row items-center justify-between">
                  <div className="text-zinc-900">
                    <Text variant="body-md">{props.microcopies.vatLabel}</Text>
                  </div>
                  <div className="text-right text-zinc-900">
                    <Text variant="body-md">{props.prices.vat}</Text>
                  </div>
                </div>
              </div>

              {/* Total (separated from compact group) */}
              <div className="mt-1 flex flex-row items-center justify-between">
                <div className="text-zinc-900">
                  <Text variant="body-lg">{props.microcopies.totalLabel}</Text>
                </div>
                <div className="text-right text-zinc-900">
                  <Text variant="body-lg">{props.prices.total}</Text>
                </div>
              </div>
            </>
          ) : (
            /* Simplified view - Total only */
            <div className="flex flex-col items-end">
              <div className="text-right text-zinc-900">
                <Text variant="body-lg">{props.prices.total}</Text>
              </div>
              <div className="text-right text-zinc-900">
                <Text variant="body-md">
                  {props.microcopies.totalAmountLabel ||
                    props.microcopies.totalLabel}
                </Text>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Total Savings Accordion Section */}
      {showSavingsSection && props.savings && (
        <div className="mx-4 mb-4 overflow-hidden rounded-md border border-stone-200 bg-accent">
          <AccordionPrimitive collapsible type="single">
            <AccordionItem className="border-b-0 px-3" value="savings">
              <AccordionTrigger className="text-zinc-900 [&>svg]:text-zinc-900">
                <span className="min-w-0 flex-1 text-left">
                  <Text variant="body-sm">
                    {props.microcopies.yourTotalSavingsLabel}
                  </Text>
                </span>
                <span className="mr-2 shrink-0">
                  <Text variant="body-sm-bold">
                    {props.savings.totalSavings}
                  </Text>
                </span>
              </AccordionTrigger>
              <AccordionContent className="text-zinc-900">
                <div className="flex w-full flex-col gap-2 overflow-hidden">
                  {props.microcopies.savingsDescriptionText && (
                    <div className="w-60 min-w-0 overflow-hidden break-words md:w-96">
                      <Text variant="body-xs">
                        {props.microcopies.savingsDescriptionText}
                      </Text>
                    </div>
                  )}
                  <div className="flex flex-col gap-1">
                    <div className="flex flex-row items-center justify-between gap-1">
                      <div className="min-w-0">
                        <Text variant="body-xs">
                          {props.microcopies.standardDealerPriceLabel}
                        </Text>
                      </div>
                      <div className="shrink-0 text-right">
                        <Text variant="body-xs">
                          {props.savings.standardDealerPrice}
                        </Text>
                      </div>
                    </div>
                    {showAnnualDiscountRow && (
                      <div className="flex flex-row items-center justify-between gap-1">
                        <div className="min-w-0">
                          <Text variant="body-xs">
                            {props.microcopies.annualDiscountSavingsLabel}
                          </Text>
                        </div>
                        <div className="shrink-0 text-right">
                          <Text variant="body-xs">
                            {props.savings.annualDiscountSavings}
                          </Text>
                        </div>
                      </div>
                    )}
                    {showPromoSavingsRow && (
                      <div className="flex flex-row items-center justify-between gap-1">
                        <div className="min-w-0">
                          <Text variant="body-xs">
                            {props.microcopies.promoSavingsLabel}
                          </Text>
                        </div>
                        <div className="shrink-0 text-right">
                          <Text variant="body-xs">
                            {props.savings.promoSavings}
                          </Text>
                        </div>
                      </div>
                    )}
                    {showExtraDiscountsRow && (
                      <div className="flex flex-row items-center justify-between gap-1">
                        <div className="min-w-0">
                          <Text variant="body-xs">
                            {props.microcopies.extraDiscountsLabel}
                          </Text>
                        </div>
                        <div className="shrink-0 text-right">
                          <Text variant="body-xs">
                            {props.savings.extraDiscounts}
                          </Text>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>
          </AccordionPrimitive>
        </div>
      )}

      {/* Bottom Action Section */}
      <div className="flex flex-col items-end gap-2 border-stone-200 border-t p-5">
        <Button
          className="shadow-base"
          disabled={props.action.disabled}
          onClick={props.action.onClick}
          size="default"
          variant="default"
        >
          {props.action.icon || <ArrowRight className="h-4 w-4" />}
          {props.action.label}
        </Button>
      </div>
    </div>
  );
}
